// Top customers tab — accounts ordered by Won $.
//
// Cell-level drill-through (SGNL-61): account name + every $ column is
// clickable. Clicking applies q=account-name + appropriate state filter.
// Sortable columns (SGNL-63): every header click toggles sort.

import { setActiveTab, setFilters } from "../state";
import type { Filters, OpportunityRow, StageState } from "../types";
import { computeTopCustomers, type TopCustomerRow } from "../lib/aggregations";
import { esc, fmtMoney } from "../lib/format";
import { bindSortHeaders, makeSortable, type SortColumn, type SortState } from "../lib/sortable";
import { drawSortableStacked } from "./charts";

const fmtPct = (v: number | null): string => (v === null ? "—" : `${(v * 100).toFixed(0)}%`);

const TOP_N = 25;

const COLUMNS: SortColumn<TopCustomerRow>[] = [
  { key: "account_name", label: "Account" },
  { key: "bu", label: "BU" },
  { key: "industry", label: "Industry" },
  { key: "deals", label: "Deals", numeric: true },
  { key: "won", label: "Won $", numeric: true },
  { key: "lost", label: "Lost $", numeric: true },
  { key: "open", label: "Open $", numeric: true },
  { key: "win_rate", label: "Win rate", numeric: true },
];

let sortState: SortState<TopCustomerRow> = { field: "won", dir: "desc" };

function drillCell(
  value: number,
  account: string | null,
  state?: StageState,
  stage?: string,
): string {
  const acct = esc(account);
  const stateAttr = state ? `data-state="${esc(state)}"` : "";
  const stageAttr = stage ? `data-stage="${esc(stage)}"` : "";
  return `<a href="#deals" class="drill-cell" data-account="${acct}" ${stateAttr} ${stageAttr}>${fmtMoney(value)}</a>`;
}

export function renderCustomersTab(host: HTMLElement, rows: OpportunityRow[]): void {
  // Bound the aggregation at TOP_N so we don't compute over the full account
  // list when only top-25 by Won $ are visible. User-driven sort then
  // re-orders that 25-row slice against any column. Per PR-Agent perf note
  // on PR #16.
  const top = computeTopCustomers(rows, TOP_N);
  const { sorted, headHtml } = makeSortable(top, COLUMNS, sortState);

  host.innerHTML = `
    <div class="tab-content" data-testid="customers-tab">
      <div class="tab-header">
        <h2>Top customers <span class="row-count">top ${top.length} by Won $</span></h2>
      </div>
      <div class="table-wrap">
        <table class="data-table sortable">
          ${headHtml}
          <tbody>
            ${sorted
              .map(
                (c) => `
              <tr>
                <td><a href="#deals" class="drill-cell" data-account="${esc(c.account_name)}">${esc(c.account_name) || "(unset)"}</a></td>
                <td>${esc(c.bu) || "—"}</td>
                <td>${esc(c.industry) || "—"}</td>
                <td class="num">${c.deals.toLocaleString()}</td>
                <td class="num">${drillCell(c.won, c.account_name, "won")}</td>
                <td class="num">${drillCell(c.lost, c.account_name, "lost", "Closed Lost")}</td>
                <td class="num">${drillCell(c.open, c.account_name, "open")}</td>
                <td class="num">${fmtPct(c.win_rate)}</td>
              </tr>`,
              )
              .join("")}
          </tbody>
        </table>
      </div>
      <div class="chart-row">
        <div class="chart-wrap"><canvas id="chart-customers-stage"></canvas></div>
      </div>
    </div>
  `;

  drawSortableStacked<TopCustomerRow>(
    "chart-customers-stage",
    top.slice(0, 12),
    "account_name",
    [
      { label: "Won $", key: "won", color: "#34b4ff" },
      { label: "Open $", key: "open", color: "#853eff" },
      { label: "Lost $", key: "lost", color: "#ff4759" },
    ],
    "Top 12 customers — Won / Open / Lost (click legend to toggle)",
  );

  for (const a of host.querySelectorAll<HTMLAnchorElement>("a.drill-cell")) {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      const account = a.dataset.account;
      const patch: Partial<Filters> = {};
      if (account && account !== "") patch.q = account;
      if (a.dataset.state) patch.state = a.dataset.state as StageState;
      // SGNL-249: stage filter is multi-select. Drill carries one
      // stage; replace prior selection with [stage].
      if (a.dataset.stage) patch.stages = [a.dataset.stage];
      setFilters(patch);
      setActiveTab("deals");
    });
  }

  bindSortHeaders(host, COLUMNS, sortState, (next) => {
    sortState = next;
    renderCustomersTab(host, rows);
  });
}
